import { Link } from 'react-router-dom';
import PolicyLayout, {
  PolicyContact,
  PolicyList,
  PolicySection,
  PolicySubsection,
} from '../components/PolicyLayout';
import { COMPANY_NAME, PHONE, SITE_DOMAIN, SITE_NAME, SITE_URL } from '../constants/site';

const UPDATED = 'May 25, 2026';

export default function PostTicketingFeesPage() {
  return (
    <PolicyLayout title="Post Ticketing Fees" lastUpdated={UPDATED}>
      <PolicySection title="1. What are post-ticketing fees?">
        <p>
          After your flight ticket is issued (&ldquo;post-ticketing&rdquo;), changes, cancellations, refunds, and special
          services are handled according to airline fare rules. {COMPANY_NAME}, operator of {SITE_NAME} ({SITE_DOMAIN}),
          may charge additional agency fees on top of any airline penalties when you request assistance through{' '}
          {SITE_URL}.
        </p>
      </PolicySection>

      <PolicySection title="2. Services that may incur post-ticketing fees">
        <PolicyList
          items={[
            'Voluntary itinerary changes (date, route, or cabin upgrades where permitted)',
            'Name corrections or passenger detail updates after ticketing',
            'Voluntary cancellations and refund requests',
            'Reissue of lost, damaged, or unreadable tickets where allowed',
            'Seat assignment, baggage prepurchase, or ancillary add-ons after booking',
            'Complex multi-city or split-ticket servicing',
          ]}
        />
      </PolicySection>

      <PolicySection title="3. Airline penalties vs. agency fees">
        <PolicySubsection title="3.1 Airline penalties">
          <p>
            Airlines may charge change or cancellation penalties based on your fare class. Basic economy and promotional
            fares often prohibit changes or charge the full fare difference plus a fee. These amounts are set by the
            airline, not {SITE_NAME}.
          </p>
        </PolicySubsection>
        <PolicySubsection title="3.2 Agency servicing fees">
          <p>
            When our agents or systems process a post-ticketing request, a servicing fee may apply per passenger, per
            ticket, or per transaction. This fee is quoted before work begins and is separate from airline penalties.
          </p>
        </PolicySubsection>
      </PolicySection>

      <PolicySection title="4. Fee disclosure and authorization">
        <p>
          For phone-assisted requests, agents will explain estimated airline costs and agency fees before charging your
          payment method. For online self-service where available, totals are shown on the confirmation step. You
          authorize charges by approving the request.
        </p>
      </PolicySection>

      <PolicySection title="5. Non-changeable and non-refundable tickets">
        <p>
          If your fare is marked non-changeable or non-refundable, post-ticketing modifications may not be permitted.
          In limited cases, airlines offer travel credits instead of cash refunds. Agency servicing fees may still apply
          when we attempt a request on your behalf, even if the airline denies the change.
        </p>
      </PolicySection>

      <PolicySection title="6. Involuntary schedule changes">
        <p>
          When an airline significantly changes or cancels your flight, different rules apply. We will help you
          understand rebooking options provided by the airline. Agency fees for involuntary disruptions are typically
          waived for straightforward reprotection when the airline initiates the change, unless you request optional
          upgrades or custom routing beyond airline offers.
        </p>
      </PolicySection>

      <PolicySection title="7. Refund processing timelines">
        <p>
          Approved refunds may take seven (7) to twenty-one (21) business days—or longer depending on the airline and
          card issuer—to appear on your statement. Agency servicing fees are generally non-refundable unless we made an
          error. See our <Link to="/flight/cancellation-refund">Cancellation &amp; Refund Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="8. How to request post-ticketing service">
        <PolicyList
          items={[
            `Call ${PHONE} (24/7) with your booking reference and passenger names.`,
            'Use My Bookings at /flight/bookings if self-service options are available for your fare.',
            'Email or contact form via our Support page — include ticket numbers and requested changes.',
          ]}
        />
      </PolicySection>

      <PolicySection title="9. Contact us">
        <PolicyContact />
      </PolicySection>
    </PolicyLayout>
  );
}
